import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Anime HOT or NOT',
    description: 'Learn about Anime HOT or NOT, the premier community-driven platform for rating and discovering anime characters. Join thousands of anime fans in rating their favorite characters.',
    url: `${siteUrl}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: 'Anime HOT or NOT',
      url: siteUrl,
      description: 'Community-driven anime character rating platform'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title="About Us - Anime HOT or NOT Character Rating Community"
        description="Learn about Anime HOT or NOT, the premier community-driven platform for rating and discovering anime characters. Join thousands of anime fans in sharing opinions and discovering the hottest anime characters."
        keywords="about anime hot or not, anime community, character rating platform, anime fans, anime rating site"
        canonical={`${siteUrl}/about`}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent mb-8 text-center">
            About Anime HOT or NOT
          </h1>

          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Anime HOT or NOT is a community-driven platform dedicated to celebrating anime characters and bringing fans together through interactive rating and discussion. Our mission is to create the most comprehensive and engaging anime character rating database where fans can express their opinions, discover new characters, and connect with fellow anime enthusiasts from around the world.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              We believe that every anime character deserves recognition, and every fan's opinion matters. Whether you're passionate about classic anime series or the latest seasonal releases, our platform provides a space where your voice can be heard and your favorite characters can shine.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              With thousands of characters from hundreds of anime series, we've built a comprehensive database that continues to grow every day. Our community of dedicated anime fans contributes ratings, comments, and insights that help others discover amazing characters they might have missed.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Makes Us Different</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl">
                  <i className="ri-community-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Community-Driven Ratings</h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Unlike algorithm-based ranking systems, our ratings come directly from real anime fans like you. Every rating reflects genuine opinions from our passionate community, making our rankings authentic and meaningful. We believe that the best way to discover great anime characters is through the collective wisdom of dedicated fans who truly understand and appreciate the medium.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl">
                  <i className="ri-database-2-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Comprehensive Database</h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    We've curated an extensive collection of anime characters spanning multiple genres, decades, and styles. From iconic protagonists to beloved supporting characters, our database includes detailed information, high-quality images, and comprehensive descriptions for each character. We continuously update our collection to include characters from the latest anime releases, ensuring our database stays current and relevant.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl">
                  <i className="ri-user-heart-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Experience</h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Create your profile, customize your avatar with your favorite character, and build your collection of up to five favorite characters. Track your rating history, engage in discussions through our comment system, and see how your opinions compare with the broader community. Our platform is designed to make your anime character rating experience as personal and enjoyable as possible.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Safe &amp; Respectful Community</h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    We're committed to maintaining a positive and respectful environment for all anime fans. Our moderation team works diligently to ensure that discussions remain constructive and welcoming. We have clear community guidelines that promote respectful interaction, and we take swift action against any form of harassment or inappropriate behavior to keep our platform safe for everyone.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How It Works</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">1. Create Your Account</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Getting started is quick and easy. Simply sign up with your email address to create your free account. Once registered, you'll have access to all our features including rating characters, leaving comments, and customizing your profile. Your account is secure and your privacy is protected with industry-standard security measures.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">2. Start Rating Characters</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Browse through our extensive collection of anime characters and rate them on a scale from 1 to 10. You can filter characters by gender preference or rate all characters. Each rating you submit contributes to the character's overall average rating and helps other fans discover popular characters. If you're unsure about a character, simply skip to the next one. You can always come back and rate characters you skipped later.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">3. Explore &amp; Discover</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Check out our Top 10 rankings to see which characters are currently the most popular in the community. Browse characters by anime series to find all characters from your favorite shows. Read detailed character descriptions and learn more about their backgrounds, personalities, and roles in their respective series. Our comprehensive character pages include ratings, statistics, and community comments.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">4. Engage with the Community</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Leave comments on character pages to share your thoughts and opinions. Reply to other users' comments to start discussions about your favorite characters. Add up to five characters to your favorites list to showcase your top picks on your profile. Customize your profile avatar using any character from our database with zoom and positioning controls to get the perfect look.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Community</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Anime HOT or NOT has grown into a thriving community of passionate anime fans from all corners of the globe. Our users come from diverse backgrounds and have varied tastes in anime, but they all share a common love for anime characters and the stories they inhabit. Whether you're a longtime anime veteran who's been watching since the golden age of anime or a newcomer just discovering the medium, you'll find a welcoming community here.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Our community members actively participate in rating characters, engaging in thoughtful discussions, and helping newcomers discover amazing anime series they might have overlooked. The diversity of opinions and perspectives in our community ensures that our ratings reflect a broad spectrum of tastes and preferences, making our platform valuable for fans of all types of anime.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              We encourage respectful debate and discussion about characters, series, and anime in general. While opinions may differ, our community guidelines ensure that all interactions remain constructive and friendly. We believe that the best anime discussions happen when fans can freely express their views while respecting others' perspectives.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Join Us Today</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Ready to start rating your favorite anime characters? Join thousands of anime fans who are already part of our community. Create your free account today and start exploring our extensive database of anime characters. Rate characters, discover new favorites, and connect with fellow fans who share your passion for anime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/rate"
                className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-2xl hover:scale-105 transition-all whitespace-nowrap text-center cursor-pointer"
              >
                Start Rating Now
              </Link>
              <Link
                to="/top10"
                className="px-8 py-4 text-lg font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap text-center cursor-pointer"
              >
                View Top 10
              </Link>
            </div>
          </div>

          <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl shadow-xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Have Questions?</h2>
            <p className="text-lg mb-6 opacity-90">
              Check out our FAQ page for answers to common questions about using Anime HOT or NOT.
            </p>
            <Link
              to="/faq"
              className="inline-block px-8 py-3 text-lg font-semibold text-pink-600 bg-white rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
            >
              Visit FAQ
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
